import { useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw } from "lucide-react";
import { Field, TextArea, TextInput, Select, PrimaryButton, GhostButton, ResultStat, ShareButton } from "@/tools/ui";

const OutBox = ({ value, testid }) => (
  <div className="border border-border bg-muted">
    <div className="flex items-center justify-between border-b border-border px-4 py-2">
      <span className="overline text-muted-foreground">Output</span>
      <div className="flex gap-2">
        <GhostButton onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
        <ShareButton testid={`${testid}-share`} text={value} />
      </div>
    </div>
    <pre data-testid={testid} className="overflow-x-auto whitespace-pre-wrap break-all px-4 py-4 font-mono-tech text-sm">{value}</pre>
  </div>
);

export const Base64Tool = () => {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("encode");
  const [out, setOut] = useState("");
  const run = () => {
    try {
      setOut(mode === "encode" ? btoa(unescape(encodeURIComponent(text))) : decodeURIComponent(escape(atob(text))));
    } catch { toast.error("Invalid input for decoding"); setOut(""); }
  };
  return (
    <div className="space-y-6">
      <Field label="Mode"><Select data-testid="base64-mode" value={mode} onChange={(e) => setMode(e.target.value)}><option value="encode">Encode</option><option value="decode">Decode</option></Select></Field>
      <Field label="Input"><TextArea data-testid="base64-input" rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type text…" /></Field>
      <PrimaryButton data-testid="base64-run" onClick={run}>{mode === "encode" ? "Encode" : "Decode"}</PrimaryButton>
      {out && <OutBox value={out} testid="base64-output" />}
    </div>
  );
};

export const UrlEncodeTool = () => {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("encode");
  const [out, setOut] = useState("");
  const run = () => {
    try { setOut(mode === "encode" ? encodeURIComponent(text) : decodeURIComponent(text)); }
    catch { toast.error("Invalid input"); }
  };
  return (
    <div className="space-y-6">
      <Field label="Mode"><Select data-testid="url-mode" value={mode} onChange={(e) => setMode(e.target.value)}><option value="encode">Encode</option><option value="decode">Decode</option></Select></Field>
      <Field label="Input"><TextArea data-testid="url-input" rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="https://example.com/?q=hello world" /></Field>
      <PrimaryButton data-testid="url-run" onClick={run}>{mode === "encode" ? "Encode" : "Decode"}</PrimaryButton>
      {out && <OutBox value={out} testid="url-output" />}
    </div>
  );
};

export const JwtDecoder = () => {
  const [token, setToken] = useState("");
  const [out, setOut] = useState(null);
  const decode = () => {
    try {
      const [h, p] = token.split(".");
      const dec = (s) => JSON.parse(decodeURIComponent(escape(atob(s.replace(/-/g, "+").replace(/_/g, "/")))));
      setOut({ header: JSON.stringify(dec(h), null, 2), payload: JSON.stringify(dec(p), null, 2) });
    } catch { toast.error("Invalid JWT"); setOut(null); }
  };
  return (
    <div className="space-y-6">
      <Field label="JWT Token"><TextArea data-testid="jwt-input" rows={4} value={token} onChange={(e) => setToken(e.target.value)} placeholder="eyJhbGciOi..." className="font-mono-tech" /></Field>
      <PrimaryButton data-testid="jwt-run" onClick={decode}>Decode Token</PrimaryButton>
      {out && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <OutBox value={out.header} testid="jwt-header" />
          <OutBox value={out.payload} testid="jwt-payload" />
        </div>
      )}
    </div>
  );
};

export const UuidGenerator = () => {
  const [count, setCount] = useState(5);
  const [out, setOut] = useState("");
  const gen = () => {
    const arr = Array.from({ length: count }, () => crypto.randomUUID());
    setOut(arr.join("\n"));
  };
  return (
    <div className="space-y-6">
      <Field label={`How many: ${count}`}><input data-testid="uuid-count" type="range" min="1" max="50" value={count} onChange={(e) => setCount(+e.target.value)} className="w-full accent-primary" /></Field>
      <PrimaryButton data-testid="uuid-run" onClick={gen}><RefreshCw className="h-4 w-4" /> Generate UUIDs</PrimaryButton>
      {out && <OutBox value={out} testid="uuid-output" />}
    </div>
  );
};

export const HashGenerator = () => {
  const [text, setText] = useState("");
  const [algo, setAlgo] = useState("SHA-256");
  const [out, setOut] = useState("");
  const run = async () => {
    const buf = await crypto.subtle.digest(algo, new TextEncoder().encode(text));
    setOut(Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join(""));
  };
  return (
    <div className="space-y-6">
      <Field label="Algorithm"><Select data-testid="hash-algo" value={algo} onChange={(e) => setAlgo(e.target.value)}><option>SHA-1</option><option>SHA-256</option><option>SHA-384</option><option>SHA-512</option></Select></Field>
      <Field label="Input"><TextArea data-testid="hash-input" rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Text to hash…" /></Field>
      <PrimaryButton data-testid="hash-run" onClick={run}>Generate Hash</PrimaryButton>
      {out && <OutBox value={out} testid="hash-output" />}
    </div>
  );
};

export const LoremIpsum = () => {
  const [count, setCount] = useState(3);
  const [out, setOut] = useState("");
  const base = "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.";
  const gen = () => setOut(Array.from({ length: count }, () => base).join("\n\n"));
  return (
    <div className="space-y-6">
      <Field label={`Paragraphs: ${count}`}><input data-testid="lorem-count" type="range" min="1" max="10" value={count} onChange={(e) => setCount(+e.target.value)} className="w-full accent-primary" /></Field>
      <PrimaryButton data-testid="lorem-run" onClick={gen}>Generate</PrimaryButton>
      {out && <OutBox value={out} testid="lorem-output" />}
    </div>
  );
};

export const TimestampConverter = () => {
  const [ts, setTs] = useState(Math.floor(Date.now() / 1000).toString());
  const [dt, setDt] = useState("");
  const toDate = () => { const d = new Date(parseInt(ts) * 1000); setDt(isNaN(d) ? "Invalid" : d.toUTCString() + "  |  " + d.toLocaleString()); };
  const now = () => setTs(Math.floor(Date.now() / 1000).toString());
  return (
    <div className="space-y-6">
      <Field label="Unix Timestamp (seconds)"><TextInput data-testid="ts-input" value={ts} onChange={(e) => setTs(e.target.value)} /></Field>
      <div className="flex gap-3">
        <PrimaryButton data-testid="ts-run" onClick={toDate}>Convert to Date</PrimaryButton>
        <GhostButton data-testid="ts-now" onClick={now}>Now</GhostButton>
      </div>
      {dt && <ResultStat label="Date (UTC | Local)" value={dt} accent />}
    </div>
  );
};
